import canvas from '@classes/canvas'
import { MeterId } from '@constants/gauges_meters_consts'
import { getPropertyValue } from '@constants/utils'
import { degreesToRadians } from '@turf/helpers'
import { isNumber } from 'lodash'

import { getUnitDx, getWindDirection } from '../helper/canvasDraw'
import type { MainParams, StyleParams } from '../Meter'
import type { GaugeDrawResult } from './gauge_types'

export function drawHeadingGauge (
  mainParams: MainParams,
  styleParams: StyleParams,
  isMeccaVisible: boolean,
  meccaLocation: number = 0,
  windDirection: number = 0,
  fontSize: number,
  layout?: string
): GaugeDrawResult {
  const { prevValue, value, ctx, center, radius, dpx, redraw, formatter, unit, currentValue } = mainParams
  const { arcColor, color } = styleParams

  const rgbColorOfLines = JSON.parse(getPropertyValue('--meter-heading-arc-rgb-color'))
  const meterColor: string = getPropertyValue('--meter-color')
  const gradient0Color: string = getPropertyValue('--heading-gauge-radial-gradient-0-color')
  const gradient1Color: string = getPropertyValue('--heading-gauge-radial-gradient-1-color')

  let lengthInDegrees: number
  let startAngle: number
  let progressAngle: number
  let newCurrentValue: number = currentValue

  lengthInDegrees = 15
  const from = Number(prevValue)
  const to = Number(value)

  const drawing = (current: number) => {
    newCurrentValue = current
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    redraw(getUnitDx(current))

    const colorOfLines = `rgb(${rgbColorOfLines.join(',')})`

    if (layout === 'grid') {
      canvas.drawArc(ctx, center, center, radius - dpx * 2, 0, Math.PI * 2, colorOfLines, dpx * 0.2)
      canvas.drawArc(ctx, center, center, radius - dpx * 10, 0, Math.PI * 2, meterColor, dpx / 2)

      for (let i = 0; i < 360; i += 5) {
        const start = canvas.xy(-current + i, center, radius - dpx * 13)
        const end = canvas.xy(-current + i, center, radius - dpx * 15)

        if (i === 0) {
          canvas.drawLine(ctx, start.x, start.y, end.x, end.y, '#ffffff', dpx * 0.5)
          const v = canvas.xy(-current + i, center, radius - dpx * 7)
          canvas.drawText(ctx, v.x, v.y, 'N', dpx * 4, color)
        } else {
          canvas.drawLine(ctx, start.x, start.y, end.x, end.y, colorOfLines, dpx * 0.2)
        }

        if (i % 30 === 0) {
          const v = canvas.xy(-current + i, center, radius - dpx * 18)
          canvas.drawText(ctx, v.x, v.y, i.toString(), dpx * 2, arcColor)
        }
      }

      for (let i = 1; i < 15; i++) {
        const r = radius - (dpx * 22 + dpx * i)
        canvas.drawArc(
          ctx,
          center,
          center,
          r,
          0,
          Math.PI * 2,
          `rgba(${rgbColorOfLines.join(',')}, ${1 - i / 10})`,
          dpx * 0.2
        )
      }

      if (gradient0Color && gradient0Color !== '' && gradient1Color && gradient1Color !== '') {
        const gradient = ctx.createRadialGradient(
          center,
          center,
          radius - (dpx * 22 + dpx * 8),
          center,
          center,
          radius - (dpx * 22 + dpx * 25)
        )

        gradient.addColorStop(0, gradient0Color)
        gradient.addColorStop(1, gradient1Color)

        canvas.drawArc(ctx, center, center, radius - (dpx * 22 + dpx * 5), 0, Math.PI * 2, 'transparent', dpx * 0.2)
        ctx.fillStyle = gradient
        ctx.fill()
      }

      if (isNumber(meccaLocation) && isMeccaVisible) {
        const start = canvas.xy(meccaLocation - current, center, radius - dpx * 1.75)
        const meccaSize = dpx
        canvas.drawMecca(ctx, start.x - meccaSize / 2, start.y - meccaSize / 2, meccaSize)
      }

      const boatSize = { width: dpx, height: radius - dpx * 6 }
      canvas.drawBoat(ctx, center - boatSize.width / 2, center - boatSize.height / 2, boatSize.width, boatSize.height)
      canvas.drawLine(ctx, center, dpx * 7, center, dpx * 8, color || '#ffffff', dpx)
      canvas.drawLine(ctx, center, dpx * 12, center, dpx * 16, color || '#ffffff', dpx)

      if (windDirection) {
        const angle = (windDirection || 0) - newCurrentValue + Number(value)
        startAngle = degreesToRadians(angle - lengthInDegrees / 2)
        progressAngle = degreesToRadians(angle)
        canvas.drawArc(
          ctx,
          center,
          center,
          radius - dpx * 10,
          startAngle,
          progressAngle,
          color || '#ffffff',
          dpx * 1.25
        )
      }

      canvas.drawValue(
        ctx,
        center + dpx * 2,
        center + dpx * 3,
        `${formatter(newCurrentValue)} ${unit}${getWindDirection(newCurrentValue)}`,
        fontSize / 2.5,
        color
      )
    } else {
      for (let i = 0; i < 360; i += 5) {
        const start = canvas.xy(i, center, radius - dpx * 2)
        const end = canvas.xy(i, center, radius - dpx * 4)
        canvas.drawLine(ctx, start.x, start.y, end.x, end.y, arcColor, dpx)
      }

      const start = canvas.xy(current, center, radius)
      const end = canvas.xy(current, center, radius - dpx * 6)
      const boatSize = { width: dpx * 1.25, height: radius * 1.3 }
      canvas.drawBoat(
        ctx,
        center - boatSize.width / 2,
        center - boatSize.height / 2,
        boatSize.width,
        boatSize.height,
        0
      )
      canvas.drawLine(ctx, start.x, start.y, end.x, end.y, arcColor || '#ffffff', dpx * 2)

      if (windDirection) {
        lengthInDegrees = 30
        const angle = (windDirection || 0) + newCurrentValue
        startAngle = degreesToRadians(angle - lengthInDegrees / 2)
        progressAngle = degreesToRadians(angle + lengthInDegrees / 2)
        canvas.drawArc(ctx, center, center, radius - dpx * 3, startAngle, progressAngle, arcColor || '#ffffff', dpx * 2)
      }

      canvas.drawValue(
        ctx,
        center + dpx * 2,
        center + dpx / 0.25,
        `${formatter(newCurrentValue)} ${unit}${getWindDirection(newCurrentValue)}`,
        fontSize / 1.5,
        color
      )
    }
  }

  const animateId = canvas.animate(from, to, drawing, 2, MeterId.HEADING)
  return { animateId, newCurrentValue }
}
